/*
 * @Author: fanzhiwei
 * @Date: 2024-11-14 09:29:43
 * @LastEditors: fanzhiwei
 * @LastEditTime: 2024-11-14 18:25:49
 * @FilePath: /common-web-ui/src/utils/http.js
 * @Description: axios
 */
import axios from 'axios'
import { Message } from 'element-ui'
import helper from './helper'
import router from '@/router'
import store from '@/store'
import { createSseEventParser, parseCourseRegenerationError } from './courseRegeneration.mjs'
axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'
// 创建axios实例
const service = axios.create({
  // axios中请求配置有baseURL选项，表示请求URL公共部分
  baseURL: '/api',
  // 超时
  timeout: 600000,
  validateStatus: (status) => {
    // 错误编码为2开头和400及404报错误提示
    return (status >= 200 && status < 300) || status === 400 || status === 404
  }
})

// ------设置请求拦截器------//
service.interceptors.request.use(
  (config) => {
    // 获取token
    const token = localStorage.getItem('token')
    // 如果不是登录和注册接口，则添加token
    if (token && !config.url.includes('/user/login') && !config.url.includes('/user/register') && !config.url.includes('/parent/add')) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

const LOGIN_PATH = '/loginNew'
const UNAUTHORIZED_CODES = [401, '401', 'UNAUTHORIZED']
let errorTimer = false
let authRedirecting = false
let licenseRedirecting = false

function isAuthenticatedRequest (config) {
  return Boolean(config?.headers?.Authorization)
}

function isUnauthorizedResponse (response) {
  return response?.status === 401 || UNAUTHORIZED_CODES.includes(response?.data?.code)
}

function isLicenseExpiredResponse (response) {
  return response?.status === 403 && response?.data?.message === 'LICENSE_EXPIRED'
}

function handleLicenseExpired () {
  if (licenseRedirecting || router.currentRoute.path === '/billing') return
  licenseRedirecting = true
  const redirect = router.currentRoute.fullPath
  Message.warning('Your learning access has expired. Please renew to continue.')
  router.replace({
    path: '/billing',
    query: { renewal: 'required', redirect }
  }).catch(() => {}).finally(() => {
    licenseRedirecting = false
  })
}

export function handleSessionExpired () {
  const hasSession = localStorage.getItem('token') || localStorage.getItem('userInfo')
  if (!hasSession || authRedirecting) return

  authRedirecting = true
  store.dispatch('user/logout')

  if (router.currentRoute.path === LOGIN_PATH) {
    authRedirecting = false
    return
  }

  const redirect = router.currentRoute.fullPath
  Message.warning('Your session has expired. Please sign in again.')
  router.replace({
    path: LOGIN_PATH,
    query: { redirect }
  }).catch(() => {}).finally(() => {
    authRedirecting = false
  })
}

// ------设置响应拦截------//
service.interceptors.response.use(
  (response) => {
    if (isLicenseExpiredResponse(response)) {
      handleLicenseExpired()
      return Promise.resolve(response)
    }
    if (isAuthenticatedRequest(response.config) && isUnauthorizedResponse(response)) {
      handleSessionExpired()
      return Promise.resolve(response)
    }

    // 对响应数据进行处理
    if (
      response.data?.code !== 200 &&
      response.config?.responseType !== 'blob'
    ) {
      // 当响应状态码不是200且响应类型不是blob时，进行错误处理
      if (!errorTimer) {
        // 防止重复错误提示
        errorTimer = true
      }
    }
    return Promise.resolve(response)
  },
  (error) => {
    if (isLicenseExpiredResponse(error.response)) {
      handleLicenseExpired()
      return Promise.reject(error)
    }
    if (isAuthenticatedRequest(error.config) && isUnauthorizedResponse(error.response)) {
      handleSessionExpired()
      return Promise.reject(error)
    }

    if (error.config?.suppressGlobalError) {
      return Promise.reject(error)
    }
    // 对响应错误进行处理
    if (error.response) {
      // 当存在响应错误时，打印错误信息
      console.log('响应拦截错误信息=>', error.response)
      const message = 'Service unavailable. Please try again.'
      if (!errorTimer) {
        // 防止重复错误提示
        errorTimer = true
        // 显示错误消息，并在关闭时重置错误提示状态
        Message.error({
          message,
          onClose: () => {
            errorTimer = false
          }
        })
      }
    }
    return Promise.reject(error)
  }
)

class Http {
  /**
   * 构造函数
   *
   * 该构造函数用于初始化axios实例
   * 它将传入的service对象赋值给新创建的实例的axios属性
   * 这样做是为了在实例中能够通过this.axios访问和使用service提供的方法和功能
   */
  constructor () {
    this.axios = service
  }

  /**
   * 根据需求返回全部response或仅返回response.data
   * @param {Object} response - 响应对象，包含响应数据
   * @param {boolean} allRes - 是否返回全部response，默认为false
   * @returns {Object|string} - 根据allRes参数决定返回全部response或仅返回response.data
   */
  transformResponse (response, allRes = false) {
    // 根据需求返回全部response或仅返回response.data
    return allRes ? response : response.data
  }

  /**
   * 封装axios请求方法
   * @param {string} method 请求方法，如 GET, POST, PUT, DELETE
   * @param {string} url 请求的URL
   * @param {Object} params 请求参数
   * @param {Object} config 请求配置
   * @param {boolean} allRes 是否返回完整的响应数据
   * @returns {Promise} 返回一个Promise对象，用于异步处理请求结果
   */
  request (method, url, params, config, allRes) {
    return new Promise((resolve, reject) => {
      this.axios
        .request({
          method: method,
          // 根据请求方法拼接参数
          url:
            method === 'GET' || method === 'DELETE'
              ? helper.queryString(url, params)
              : url,
          // 根据请求方法设置data
          data: method === 'POST' || method === 'PUT' ? params : null,
          ...config
        })
        .then(
          (response) => {
            // 请求成功时，解析响应数据
            resolve(this.transformResponse(response, allRes))
          },
          (error) => {
            // 请求失败时，将错误信息返回
            reject(error)
          }
        )
        .catch((error) => {
          // 捕获网络错误等异常情况
          reject(error)
        })
    })
  }

  // ------对request二次封装------//
  /**
   * @param {String} url 请求服务器地址
   * @param {Object} params 传参
   * @param {Object} config 自定义配置
   * @param {Boolean} allRes 是否全量返回response对象。默认返回response.data
   *
   */
  get ({ url, params = {}, config = {}, allRes }) {
    // 封装GET请求方法
    return this.request('GET', url, params, config, allRes)
  }

  post ({ url, params = {}, config = {}, allRes }) {
    // 封装POST请求方法
    return this.request('POST', url, params, config, allRes)
  }

  /**
   * @description 流式请求
   * @param {String} url 请求地址
   * @param {Object} params 请求参数
   * @param {Function} onMessage 消息回调函数
   * @param {Object} config 请求配置
   */
  stream ({ url, params = {}, onMessage, config = {} }) {
    return new Promise((resolve, reject) => {
      // 用于追踪已处理的响应文本长度
      let processedLength = 0

      this.axios
        .request({
          method: 'POST',
          url,
          data: params,
          responseType: 'text',
          onDownloadProgress: (progressEvent) => {
            const responseText = progressEvent.currentTarget.responseText
            if (!responseText || responseText.length <= processedLength) {
              return
            }

            // 只处理新的数据部分
            const newText = responseText.slice(processedLength)
            processedLength = responseText.length

            // 按行分割新的响应文本
            const lines = newText.split('\n')
            lines.forEach(line => {
              if (line.startsWith('data:')) {
                try {
                  // 处理双 data: 前缀的情况
                  const cleanLine = line.replace(/^data:data:/, 'data:').replace(/^data:/, '').trim()

                  // 跳过 [DONE] 标记
                  if (cleanLine === '[DONE]') {
                    return
                  }

                  if (cleanLine) {
                    const data = JSON.parse(cleanLine)
                    // 只提取 content 部分
                    if (data.choices && data.choices[0] && data.choices[0].delta && data.choices[0].delta.content) {
                      onMessage(data.choices[0].delta.content)
                    }
                  }
                } catch (e) {
                  // 忽略 [DONE] 解析错误
                  if (!line.includes('[DONE]')) {
                    console.error('解析响应数据失败:', e, line)
                  }
                }
              }
            })
          },
          ...config
        })
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  streamSse ({ url, params = {}, onMessage, config = {} }) {
    return new Promise((resolve, reject) => {
      let processedLength = 0
      const parser = createSseEventParser((event) => {
        if (onMessage) onMessage(event)
      })

      this.axios
        .request({
          method: 'POST',
          url,
          data: params,
          responseType: 'text',
          ...config,
          onDownloadProgress: (progressEvent) => {
            const target = progressEvent.currentTarget || progressEvent.target
            const responseText = target && target.responseText
            if (!responseText || responseText.length <= processedLength) {
              return
            }

            const newText = responseText.slice(processedLength)
            processedLength = responseText.length
            parser.push(newText)
          }
        })
        .then((response) => {
          const responseText = typeof response.data === 'string' ? response.data : ''
          if (responseText.length > processedLength) {
            parser.push(responseText.slice(processedLength))
          }
          parser.finish()
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  streamNew ({ url, params = {}, onMessage, config = {} }) {
    let hasSeenStageDone = false
    let streamError = null
    return this.streamSse({
      url,
      params,
      config,
      onMessage: (event) => {
        const trimmed = event.trim()
        const parsedError = parseCourseRegenerationError(trimmed)
        if (parsedError) {
          streamError = parsedError
          return
        }
        if (trimmed === '[STAGE_DONE]') {
          hasSeenStageDone = true
          return
        }
        if (trimmed === '[DONE]') return
        if (hasSeenStageDone) hasSeenStageDone = false
        if (onMessage) onMessage(event)
      }
    }).then((result) => {
      if (streamError) {
        const error = new Error(streamError.message)
        error.code = streamError.code
        throw error
      }
      return result
    })
  }
}

export default new Http()
